import React, { useEffect, useState, useRef } from "react";
import api from "../../api/api";
import "../../css/Employee.css";

const MeetingListdata = () => {
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage, setEmployeesPerPage] = useState(10);
  const [showListFilter, setShowListFilter] = useState(false);

  const [zoomImageUrl, setZoomImageUrl] = useState(null);

  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  const dateInputRef = useRef(null);
  const listFilterRef = useRef(null);

  // New state for meeting popup
  const [popupMeetings, setPopupMeetings] = useState([]);
  const [popupEmployeeName, setPopupEmployeeName] = useState("");

  useEffect(() => {
    api
      .get("/employee/meeting")
      .then((response) => {
        if (response.data.success) {
          setEmployees(response.data.data);
          setFilteredEmployees(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching employees:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    api
      .get("/get/state")
      .then((response) => {
        if (response.data.success) {
          setStates(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching states:", error);
      });
  }, []);

  useEffect(() => {
    const normalizeString = (str = "") =>
      (str || "").toString().trim().replace(/\s+/g, " ").toLowerCase();
  
    let dataToFilter = employees;
  
    if (searchTerm.trim() !== "") {
      const search = normalizeString(searchTerm);
  
      dataToFilter = employees.filter((emp) => {
        const name = normalizeString(emp.name);
        const email = normalizeString(emp.email);
        const company = normalizeString(emp.company);
        const department = normalizeString(emp.department);
        const designation = normalizeString(emp.designation);
        const state = normalizeString(emp.state);
        const phone = normalizeString(emp.phone);
  
        const todaysMeetings = emp.meetings?.filter(
          (m) => m.date === selectedDate
        ) ?? [];
  
        const meetingMatch =
          todaysMeetings.some((m) => {
            const dealer = normalizeString(m.dealer);
            const data = normalizeString(m.data);
            const time = normalizeString(m.time);
            const date = normalizeString(m.date);
            const agent_type = normalizeString(m.agent_type);
  
            return (
              dealer.includes(search) ||
              data.includes(search) ||
              time.includes(search) ||
              date.includes(search) ||
              agent_type.includes(search)
            );
          }) ?? false;
  
        return (
          (name.includes(search) ||
            email.includes(search) ||
            company.includes(search) ||
            department.includes(search) ||
            designation.includes(search) ||
            state.includes(search) ||
            phone.includes(search) ||
            meetingMatch) &&
          todaysMeetings.length > 0
        );
      });
    } else {
      if (selectedDate) {
        dataToFilter = dataToFilter.filter((emp) =>
          emp.meetings?.some((m) => m.date === selectedDate)
        );
      }
      if (selectedState) {
        dataToFilter = dataToFilter.filter(
          (emp) => normalizeString(emp.state) === normalizeString(selectedState)
        );
      }
    }
  
    setFilteredEmployees(dataToFilter);
    setCurrentPage(1);
  }, [searchTerm, selectedDate, employees, selectedState]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listFilterRef.current &&
        !listFilterRef.current.contains(event.target)
      ) {
        setShowListFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredEmployees.slice(
    indexOfFirstEmployee,
    indexOfLastEmployee
  );
  const totalPages = Math.ceil(filteredEmployees.length / employeesPerPage);

  const handlePerPageChange = (value) => {
    setEmployeesPerPage(value);
    setShowListFilter(false);
    setCurrentPage(1);
  };

  const closeZoomModal = () => {
    setZoomImageUrl(null);
  };

  // Modal open handler
  const openMeetingPopup = (meetings, empName) => {
    setPopupMeetings(meetings);
    setPopupEmployeeName(empName);
  };

  const closeMeetingPopup = () => {
    setPopupMeetings([]);
    setPopupEmployeeName("");
  };

  const parseBrands = (brandStr) => {
    try {
      const parsed = JSON.parse(brandStr || "[]");
      return Array.isArray(parsed) ? parsed.join(", ") : "N/A";
    } catch {
      return "N/A";
    }
  };

  return (
    <section className="employee-section">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Employee Meeting List</h2>
          <div className="right-filter">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="state-select tooltip-wrapper" data-tooltip="Filter by State">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="">All States</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_title}>
                    {state.state_title}
                  </option>
                ))}
              </select>
            </div>

            <div className="date-filter tooltip-wrapper" data-tooltip="Filter by Date">
              <button
                onClick={() =>
                  dateInputRef.current && dateInputRef.current.showPicker()
                }
                title="Select Date"
              >
                <i className="fa-solid fa-calendar"></i>
              </button>

              <input
                ref={dateInputRef}
                type="date"
                value={selectedDate}
                onChange={(e) =>
                  setSelectedDate(e.target.value || getTodayDate())
                }
                max={getTodayDate()}
                style={{
                  position: "absolute",
                  right: 0,
                  opacity: 0,
                  pointerEvents: "none",
                  width: "1px",
                  height: "1px",
                }}
              />
            </div>

            <div className="list-fliter tooltip-wrapper" ref={listFilterRef} data-tooltip="Filter by List">
              <button onClick={() => setShowListFilter(!showListFilter)}>
                <i className="fa-solid fa-filter"></i>
              </button>
              {showListFilter && (
                <div className="filter-data">
                  {[10, 30, 50, 100].map((number) => (
                    <div
                      key={number}
                      className={`filter-option ${
                        employeesPerPage === number ? "selected" : ""
                      }`}
                      onClick={() => handlePerPageChange(number)}
                    >
                      {number}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Full Name</th>
                    <th>Phone</th>
                    <th>Company</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Date</th>
                    <th>State</th>
                    <th>Daily Count</th>
                    <th className="metting-001">Meetings</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEmployees.map((emp, index) => {
                    const todaysMeetings =
                      emp.meetings?.filter((m) => m.date === selectedDate) ??
                      [];

                    return (
                      <tr key={emp.employee_id || index}>
                        <td>{indexOfFirstEmployee + index + 1}</td>
                        <td>{emp.name}</td>
                        <td className="phone"><i class="fa-solid fa-phone"></i>&nbsp; {emp.phone}</td>
                        <td className="p-day">{emp.company}</td>
                        <td>{emp.department}</td>
                        <td>{emp.designation}</td>
                        <td className="check-in">{todaysMeetings[0]?.date || "N/A"}</td>
                        <td className="state">{emp.state}</td>
                        <td>
                          {emp.daily_meeting_summary?.find(
                            (summary) => summary.date === selectedDate
                          )?.meeting_count || 0}
                        </td>
                        <td>
                          <div className="view-btn-010">
                            <button
                              className="view-meeting-btn"
                              onClick={() =>
                                openMeetingPopup(todaysMeetings, emp.name)
                              }
                            >
                              <i className="fa-regular fa-eye"></i>&nbsp;View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}

        {zoomImageUrl && (
          <div className="zoom-modal-overlay" onClick={closeZoomModal}>
            <div
              className="zoom-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-btn" onClick={closeZoomModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
              <img src={zoomImageUrl} alt="Zoomed" className="zoomed-image" />
            </div>
          </div>
        )}

        {popupMeetings.length > 0 && (
          <div
            className="zoom-modal-overlay meeting-popup"
            onClick={closeMeetingPopup}
          >
            <div
              className="zoom-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="top-header-001">
                <button className="close-btn" onClick={closeMeetingPopup}>
                  <i className="fa-solid fa-xmark"></i>
                </button>
                <h3>Meetings for {popupEmployeeName}</h3>
              </div>
              <div className="bottom-row-wrapper">
                <div className="meeting-row">
                  {popupMeetings.map((meeting, i) => (
                    <div key={i} className="main-metting">
                      {meeting.role === "Farmer" ? (
                        <>
                          <div>
                            <strong>Type:</strong> {meeting.dealer}
                          </div>
                          <div>
                            <strong>Farmer Name:</strong> {meeting.farmer_name}
                          </div>
                          <div className="phone">
                            <strong>Phone No:</strong> {meeting.phone}
                          </div>
                          <div className="dob">
                            <strong>Date of Birth:</strong> {meeting.dob}
                          </div>
                          <div>
                            <strong>Anniversary Date:</strong>{" "}
                            {meeting.anniversay_date}
                          </div>
                          <div>
                            <strong>Address:</strong> {meeting.address}
                          </div>
                          <div className="b1">
                            <strong>Buffalo:</strong> {meeting.cattle_buff}
                          </div>
                          <div className="b2">
                            <strong>Cow:</strong> {meeting.cattle_cow}
                          </div>
                          <div className="b3">
                            <strong>Calf:</strong> {meeting.cattle_calf}
                          </div>
                          <div className="b3">
                            <strong>Trail Bag:</strong> {meeting.trail_bag}
                          </div>
                          <div>
                            <strong>Next Meeting:</strong> {meeting.schedule}
                          </div>
                          <div>
                            <strong>Note:</strong>{" "}
                            {meeting.meeting_note}
                          </div>
                        </>
                      ) : meeting.role === "Dealer" ? (
                        <>
                          <div>
                            <strong>Type:</strong> {meeting.dealer}
                          </div>
                          <div>
                            <strong>Dealer Name:</strong> {meeting.dealer_name}
                          </div>
                          <div>
                            <strong>Firm Name:</strong> {meeting.firm_name}
                          </div>
                          <div className="brand">
                            <strong>Brands:</strong>{" "}
                            {parseBrands(meeting.brand)}
                          </div>
                          <div className="sale">
                            <strong>Sales:</strong> {meeting.sale}
                          </div>
                         
                          <div>
                            <strong>Note:</strong>{" "}
                            {meeting.meeting_note || "N/A"}
                          </div>
                        </>
                      ) : (
                        <div>
                          <em>Unknown role: {meeting.role}</em>
                        </div>
                      )}

                      <div className="m-state">
                        <strong>State:</strong> {meeting.state}
                      </div>
                      <div className="date">
                        <strong>Date:</strong> {meeting.date}
                      </div>
                      <div>
                        <strong>Time:</strong> {meeting.time}
                      </div>
                      <div>
                        <strong>Device:</strong> {meeting.agent_type}
                      </div>
                      <div className="d-img">
                        <span>
                          <strong>Image:</strong>
                        </span>
                        <div>
                          {meeting.image ? (
                            <i
                              className="fa-solid fa-images"
                              style={{
                                fontSize: "30px",
                                color: "#5b8c51",
                                cursor: "pointer",
                                marginTop: "5px",
                              }}
                              onClick={() => setZoomImageUrl(meeting.image)}
                              title="Click to view image"
                            />
                          ) : (
                            <em>No Image</em>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {zoomImageUrl && (
        <div className="zoom-modal-overlay" onClick={closeZoomModal}>
          <div
            className="zoom-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-btn" onClick={closeZoomModal}>
              <i className="fa-solid fa-xmark"></i>
            </button>
            <img src={zoomImageUrl} alt="Zoomed" className="zoomed-image" />
          </div>
        </div>
      )}
    </section>
  );
};

export default MeetingListdata;
